import { ReactNode, useMemo } from 'react';
import { LoadingSkeleton } from '@coinbase/cdp-react/components/ui/LoadingSkeleton';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import ControlBtnLink from 'apps/cdp-web-demo/app/ControlBtnLink';
import {
  BASE_SEPOLIA_FAUCET_URL,
  SOLANA_DEVNET_FAUCET_URL,
} from 'apps/cdp-web-demo/app/data/faucet';
import { AccountCreationType } from 'apps/cdp-web-demo/app/types';
import { Big } from 'big.js';
import { formatEther } from 'viem';

import { Card } from '../Card/Card';

import styles from './BalanceCard.module.css';

type Props = {
  accountCreationType: AccountCreationType;
  balance?: bigint;
  isPendingBalance?: boolean;
  children?: ReactNode;
  userAddress?: string;
};

function formatSol(lamports: bigint) {
  return Big(Number(lamports))
    .div(LAMPORTS_PER_SOL)
    .toFixed(9, Big.roundDown)
    .replace(/\.?0+$/, '');
}

function formatEth(wei: bigint) {
  return Big(formatEther(wei))
    .toFixed(8, Big.roundDown)
    .replace(/\.?0+$/, '');
}

function BalanceCard({
  accountCreationType,
  balance,
  children,
  isPendingBalance,
  userAddress,
}: Props) {
  const isSolana = accountCreationType === 'solana';
  const token = isSolana ? 'SOL' : 'ETH';
  const devToken = isSolana ? 'devnet SOL' : 'testnet ETH';
  const faucetUrl = isSolana ? SOLANA_DEVNET_FAUCET_URL : BASE_SEPOLIA_FAUCET_URL;

  const formattedBalance = useMemo(() => {
    if (balance === undefined) return undefined;

    // Convert the balance to the appropriate format
    let formatted = isSolana ? formatSol(balance) : formatEth(balance);
    // If the balance is greater than 1000, format it in compact notation
    if (Big(formatted).gt(1000)) {
      const formatter = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        roundingMode: 'floor',
      });
      formatted = formatter.format(Number(formatted));
    }

    return formatted;
  }, [balance, isSolana]);

  const hasBalance = useMemo(() => {
    return !!balance && balance !== 0n;
  }, [balance]);

  return (
    <Card heading="Balance">
      {isPendingBalance && (
        <>
          <LoadingSkeleton className={styles['loading-balance']} />
          <LoadingSkeleton className={styles['loading-btn']} />
          <LoadingSkeleton className={styles['loading-btn']} />
        </>
      )}
      {!isPendingBalance && (
        <>
          <p className={styles.balance}>
            {formattedBalance ?? 0} {token}
          </p>
          {!hasBalance && (
            <p className={styles['no-balance']}>
              You need some {token} before you can send a transaction.
            </p>
          )}
          <div>
            <ControlBtnLink data-faucet-link href={`${faucetUrl}&address=${userAddress}`}>
              <span>Get {devToken}</span>
            </ControlBtnLink>
          </div>
        </>
      )}
      {children}
    </Card>
  );
}

export { BalanceCard };
